import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { Login as LoginIcon } from '@mui/icons-material';

import { BaseForm } from './BaseForm';
import { FormField } from './FormField';
import { loginSchema, LoginFormData } from '@/utils/validation.ts';

interface LoginFormProps {
  onSubmit: (data: LoginFormData) => void | Promise<void>;
  loading?: boolean;
  error?: string;
  onForgotPassword?: () => void;
  onRegister?: () => void;
}

export const LoginForm = ({
  onSubmit,
  loading = false,
  error,
  onForgotPassword,
  onRegister,
}: LoginFormProps) => {
  const [showPassword, setShowPassword] = useState(false);

  const defaultValues: Partial<LoginFormData> = {
    email: '',
    password: '',
    rememberMe: false,
  };

  return (
    <BaseForm
      title="Вход в систему"
      schema={loginSchema}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      loading={loading}
    >
      {(form: UseFormReturn<LoginFormData>) => (
        <>
          {error && (
            <Alert variant="filled" severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <FormField
            name="email"
            control={form.control}
            label="Email"
            type="email"
            placeholder="Введите ваш email"
          />

          <FormField
            name="password"
            control={form.control}
            label="Пароль"
            type={showPassword ? 'text' : 'password'}
            placeholder="Введите пароль"
          />

          <Box display="flex" alignItems="center" gap={1}>
            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? 'Скрыть' : 'Показать'} пароль
            </Button>
          </Box>

          <FormField
            name="rememberMe"
            control={form.control}
            label="Запомнить меня"
            checkbox
          />

          {onForgotPassword && (
            <Box textAlign="right">
              <Link
                component="button"
                type="button"
                onClick={onForgotPassword}
                variant="body2"
              >
                Забыли пароль?
              </Link>
            </Box>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            startIcon={loading ? <CircularProgress size={20} /> : <LoginIcon />}
            disabled={loading || !form.formState.isValid}
            size="large"
          >
            {loading ? 'Вход...' : 'Войти'}
          </Button>

          {onRegister && (
            <Box textAlign="center" sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Нет аккаунта?{' '}
                <Link
                  component="button"
                  type="button"
                  onClick={onRegister}
                  variant="body2"
                >
                  Зарегистрироваться
                </Link>
              </Typography>
            </Box>
          )}
        </>
      )}
    </BaseForm>
  );
};
